import React, { FC, useCallback, useMemo, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import { Box, Divider, Heading } from '@chakra-ui/react';
import { Callout } from '@shinetools/sunshine-universal';

import {
  CreateInternationalTransferRecipientInput,
  SensitiveOperationType,
} from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useDebouncedCallback from 'common/hooks/useDebouncedCallback';
import Button from 'components/_core/Button';
import BackButton from 'components/BackButton';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import SMSVerificationModal from 'components/SMSVerificationModal';
import useToast from 'components/ToastProvider/useToast';
import TrackView from 'features/Analytics/TrackView';
import ErrorLayout from 'layouts/ErrorLayout';
import * as Layout from 'layouts/StepperLayout';

import { useBankTransferContext } from '../../../../../../contexts';
import { InternationalTransferDynamicForm } from '../../../../components/InternationalTransferDynamicForm';
import { Recipient } from '../../types';
import { InternationalTransferRecipientNetworkRequirementsDocument } from './graphql/internationalTransferRecipientNetworkRequirements.gql';
import { useCreateInternationalTransferRecipient } from './hooks/useCreateInternationalTransferRecipient';
import locales from './locales';

const RECIPIENT_FORM_ID = 'internationalTransferRecipientForm';

interface NewInternationalTransferRecipientProps {
  onBackClick: () => void;
  onRecipientCreated: (recipient: Recipient) => void;
  variant: 'new' | 'edit';
}

export const NewInternationalTransferRecipient: FC<
  NewInternationalTransferRecipientProps
> = ({ onBackClick, onRecipientCreated, variant = 'new' }) => {
  const { companyProfileId, companyUserId } = useCompanyContext();
  if (!companyProfileId) {
    throw new Error(
      'companyProfileId is required in InternationalTransferRecipient',
    );
  }
  if (!companyUserId) {
    throw new Error(
      'companyUserId is required in InternationalTransferRecipient',
    );
  }

  const { showToast } = useToast();
  const language = locales.getLanguage();
  const {
    bankTransfer: {
      quote: { internationalTransferQuoteId, ...quote },
    },
  } = useBankTransferContext('internationalTransferRecipient');
  const requirementsQueryInput = useMemo(
    () => ({
      companyProfileId,
      companyUserId,
      internationalTransferQuoteId,
      lang: language,
    }),
    [companyProfileId, companyUserId, internationalTransferQuoteId, language],
  );
  const { data, error, refetch } = useQuery(
    InternationalTransferRecipientNetworkRequirementsDocument,
    {
      variables: {
        input: requirementsQueryInput,
      },
    },
  );
  const { createInternationalTransferRecipient, errors: formErrors } =
    useCreateInternationalTransferRecipient();
  const [isSmsVerificationModalOpen, setIsSmsVerificationModalOpen] =
    useState(false);
  const [createRecipientInput, setCreateRecipientInput] =
    useState<CreateInternationalTransferRecipientInput>();

  const refetchDebounced = useDebouncedCallback(refetch, 500);
  const handleFormRefresh = useCallback(
    (values: FieldValues) => {
      refetchDebounced({
        input: {
          ...requirementsQueryInput,
          requirements: values,
        },
      });
    },
    [refetchDebounced, requirementsQueryInput],
  );

  if (error) {
    return (
      <ErrorLayout>
        <ErrorCard retry={refetch} />
      </ErrorLayout>
    );
  }

  if (!data) {
    return <Loader />;
  }

  const { internationalTransferRecipientNetworkRequirements: formConfigs } =
    data;

  const handleFormSubmit = (type: string, values: FieldValues) => {
    setCreateRecipientInput({
      companyProfileId,
      companyUserId,
      currency: quote.target.currency,
      internationalTransferQuoteId,
      lang: language,
      providerRecipientType: type,
      requirements: values,
    });
    setIsSmsVerificationModalOpen(true);
  };

  const handleSuccessfulSMSVerification = async (token: string) => {
    if (!createRecipientInput) {
      throw new Error('createRecipientInput is required');
    }

    try {
      const { errors, recipient } = await createInternationalTransferRecipient(
        createRecipientInput,
        token,
      );

      if (recipient) {
        showToast({
          message: locales.formatString(locales.recipientCreationSuccess, {
            beneficiaryName: recipient.fullName,
          }),
          type: 'success',
        });
        onRecipientCreated(recipient);
        return;
      }

      if (errors) {
        setCreateRecipientInput(undefined);
      }
      return;
    } catch (e) {
      showToast({ message: locales.failedToCreateRecipient, type: 'error' });
      setCreateRecipientInput(undefined);
    }
  };

  return (
    <>
      {createRecipientInput ? (
        <SMSVerificationModal
          actionData={createRecipientInput}
          actionType={SensitiveOperationType.CreateBeneficiary}
          isOpen={isSmsVerificationModalOpen}
          onClose={() => setIsSmsVerificationModalOpen(false)}
          onSuccess={handleSuccessfulSMSVerification}
        />
      ) : null}
      <Layout.Root>
        <Layout.Body>
          <TrackView pageName="NewInternationalTransferRecipient" />
          <Layout.HeaderButtonsContainer>
            <BackButton onClick={onBackClick} />
          </Layout.HeaderButtonsContainer>
          <Layout.BodyScrollableSection paddingBottom="space-48">
            <Heading alignSelf="flex-start" size="lg" textAlign="left">
              {locales.title}
            </Heading>
            {variant === 'edit' ? (
              <Box paddingTop="space-32">
                <Callout variant="warning" withIcon>
                  <Callout.Title>
                    {locales.forcedUpdateCallout.title}
                  </Callout.Title>
                  <Callout.Content>
                    {locales.forcedUpdateCallout.body}
                  </Callout.Content>
                </Callout>
              </Box>
            ) : null}
            <InternationalTransferDynamicForm
              configs={formConfigs}
              errors={formErrors}
              id={RECIPIENT_FORM_ID}
              onRefresh={handleFormRefresh}
              onSubmit={handleFormSubmit}
            />
          </Layout.BodyScrollableSection>
          <Layout.FooterContainer>
            <Divider />
            <Layout.FooterButtonsContainer>
              <Button
                form={RECIPIENT_FORM_ID}
                icon="arrow-right"
                iconPosition="right"
                type="submit"
              >
                {locales.submit[variant]}
              </Button>
            </Layout.FooterButtonsContainer>
          </Layout.FooterContainer>
        </Layout.Body>
      </Layout.Root>
    </>
  );
};
